import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { LogNotification, LogNotifyBroadcast, LogNotifyGame } from '@/api';
import { getNotificationMessage } from '../model/getNotificationMessage';
import { ChallengeNotificationActions } from './ChallengeNotificationActions';
import { NotificationIcon } from './NotificationIcon';
import CheckIcon from './assets/check.svg?react';
import RemoveDoneIcon from './assets/remove_done.svg?react';
import TrashIcon from './assets/trash.svg?react';
import styles from './styles.module.scss';
import { Button } from '@igo/ui';
import { isDemonstrationStartedSubtype } from '../model/notificationSubtypes';

type Props = {
    title: string;
    items: LogNotification[];
    onOpen: (notification: LogNotification) => void;
    onToggleRead: (id: string) => void;
    onDelete: (id: string) => void;
    onChallengeAccepted: (gameId: string) => void;
    tooltipsEnabled?: boolean;
};

const isIncomingChallenge = (notification: LogNotification) =>
    notification.game === LogNotifyGame.NOTIFY_GAME_CHALLENGE_DIRECT ||
    notification.game === LogNotifyGame.NOTIFY_GAME_CHALLENGE_DIRECT_RANKED;

const isAcceptedChallenge = (notification: LogNotification) =>
    notification.game === LogNotifyGame.NOTIFY_GAME_CHALLENGE_ACCEPT && !!notification.messageData?.link_id;

const isStartedBroadcast = (notification: LogNotification) =>
    notification.broadcast === LogNotifyBroadcast.NOTIFY_BROADCAST_START && !!notification.messageData?.link_id;

const isStartedDemonstration = (notification: LogNotification) =>
    isDemonstrationStartedSubtype(notification.broadcast) && !!notification.messageData?.link_id;

const isDemonstrationInvite = (notification: LogNotification) =>
    notification.broadcast === LogNotifyBroadcast.NOTIFY_BROADCAST_INVITE && !!notification.messageData?.link_id;

export const NotificationGroup = ({
    title,
    items,
    onOpen,
    onToggleRead,
    onDelete,
    onChallengeAccepted,
    tooltipsEnabled = true,
}: Props) => {
    const { t, i18n } = useTranslation('global', { keyPrefix: 'notifications' });

    if (items.length === 0) return null;

    const formatDate = (value?: string) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const days = Math.round((startOfDate - startOfToday) / 86_400_000);

        if (days === 0) {
            return new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(date);
        }
        if (days >= -6) {
            return new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(days, 'day');
        }
        return new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(date);
    };

    return (
        <section className={styles.group}>
            <h3 className={styles.groupTitle}>{title}</h3>
            <div>
                {items.map((notification) => (
                    <article
                        key={notification.id}
                        className={classNames(styles.item, { [styles.itemRead]: !!notification.readAt })}
                        onClick={() => onOpen(notification)}
                        onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onOpen(notification);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <NotificationIcon notification={notification} />
                        <div className={styles.itemContent}>
                            <div className={styles.itemHeading}>
                                <span className={styles.itemType}>{getNotificationMessage(notification, t)}</span>

                                {!notification.readAt && isIncomingChallenge(notification) && (
                                    <ChallengeNotificationActions
                                        notification={notification}
                                        onMarkAsRead={onToggleRead}
                                        onAccepted={onChallengeAccepted}
                                    />
                                )}
                                {(isAcceptedChallenge(notification) ||
                                    isStartedBroadcast(notification) ||
                                    isStartedDemonstration(notification) ||
                                    isDemonstrationInvite(notification)) && (
                                    <div
                                        className={styles.challengeAction}
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                    >
                                        <Button variant="secondary" onClick={() => onOpen(notification)}>
                                            {isDemonstrationInvite(notification) || isStartedDemonstration(notification)
                                                ? t('openDemonstration')
                                                : isStartedBroadcast(notification)
                                                  ? t('openBroadcast')
                                                  : t('openGame')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.itemMeta}>
                            <time className={styles.time}>{formatDate(notification.publishedAt)}</time>
                            <div className={styles.itemActions}>
                                {!notification.readAt && notification.id && (
                                    <button
                                        type="button"
                                        className={styles.toggleReadNotification}
                                        aria-label={t('markAsRead')}
                                        data-tooltip-id={tooltipsEnabled ? 'notification-center-tooltip' : undefined}
                                        data-tooltip-content={tooltipsEnabled ? t('markAsRead') : undefined}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onToggleRead(notification.id!);
                                        }}
                                    >
                                        <CheckIcon />
                                    </button>
                                )}
                                {notification.readAt && notification.id && (
                                    <button
                                        type="button"
                                        className={styles.toggleReadNotification}
                                        aria-label={t('markAsUnread')}
                                        data-tooltip-id={tooltipsEnabled ? 'notification-center-tooltip' : undefined}
                                        data-tooltip-content={tooltipsEnabled ? t('markAsUnread') : undefined}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onToggleRead(notification.id!);
                                        }}
                                    >
                                        <RemoveDoneIcon />
                                    </button>
                                )}
                                {notification.id && (
                                    <button
                                        type="button"
                                        className={styles.deleteNotification}
                                        aria-label={t('delete')}
                                        data-tooltip-id={tooltipsEnabled ? 'notification-center-tooltip' : undefined}
                                        data-tooltip-content={tooltipsEnabled ? t('delete') : undefined}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDelete(notification.id!);
                                        }}
                                    >
                                        <TrashIcon />
                                    </button>
                                )}
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
};
